import { useState, useEffect } from 'react'
import { X, Mail, Server, Send, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react'
import { useCreateMailbox, useUpdateMailbox, useTestConnection } from '@hooks/useMailboxes'
import { Mailbox } from '@/types'
import {
  CreateMailboxRequest,
  UpdateMailboxRequest,
  AwsSesConfig,
  SmtpConfig,
  SendGridConfig,
} from '@services/mailbox.service'

/**
 * MailboxForm Component
 * Multi-Provider Email Account Configuration Form
 * Version: 2.0.0 (AWS SES, SMTP, SendGrid Support)
 */

interface MailboxFormProps {
  mailbox?: Mailbox | null
  onClose: () => void
  onSuccess: () => void
}

type Provider = 'AWS_SES' | 'SMTP' | 'SENDGRID'

interface FormData {
  // Basic Information
  name: string
  email: string
  fromName: string
  replyToEmail: string
  provider: Provider
  dailyLimit: number
  status: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED' | 'ERROR'
  notes: string

  // Provider Configs
  awsSesConfig: AwsSesConfig
  smtpConfig: SmtpConfig
  sendgridConfig: SendGridConfig
}

const DEFAULT_FORM_DATA: FormData = {
  name: '',
  email: '',
  fromName: '',
  replyToEmail: '',
  provider: 'SMTP',
  dailyLimit: 40,
  status: 'ACTIVE',
  notes: '',
  awsSesConfig: {
    region: 'us-east-1',
    accessKeyId: '',
    secretAccessKey: '',
    configurationSet: '',
  },
  smtpConfig: {
    host: '',
    port: 587,
    secure: false,
    auth: {
      user: '',
      pass: '',
    },
    imap: {
      host: '',
      port: 993,
      secure: true,
      auth: {
        user: '',
        pass: '',
      },
    },
  },
  sendgridConfig: {
    apiKey: '',
  },
}

export function MailboxForm({ mailbox, onClose, onSuccess }: MailboxFormProps) {
  const [formData, setFormData] = useState<FormData>(DEFAULT_FORM_DATA)
  const [testStatus, setTestStatus] = useState<'idle' | 'testing' | 'success' | 'error'>('idle')
  const [testMessage, setTestMessage] = useState('')

  const createMutation = useCreateMailbox()
  const updateMutation = useUpdateMailbox(mailbox?.id || 0)
  const testConnectionMutation = useTestConnection()

  const isEditing = Boolean(mailbox)
  const isLoading = createMutation.isPending || updateMutation.isPending

  // Load existing mailbox data
  useEffect(() => {
    if (mailbox) {
      setFormData({
        name: mailbox.name,
        email: mailbox.email,
        fromName: mailbox.fromName || '',
        replyToEmail: mailbox.replyToEmail || '',
        provider: mailbox.provider,
        dailyLimit: mailbox.dailyLimit,
        status: mailbox.status,
        notes: mailbox.notes || '',
        awsSesConfig: DEFAULT_FORM_DATA.awsSesConfig,
        smtpConfig: DEFAULT_FORM_DATA.smtpConfig,
        sendgridConfig: DEFAULT_FORM_DATA.sendgridConfig,
      })
    }
  }, [mailbox])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const baseData = {
      name: formData.name,
      email: formData.email,
      fromName: formData.fromName || undefined,
      replyToEmail: formData.replyToEmail || undefined,
      provider: formData.provider,
      dailyLimit: formData.dailyLimit,
      notes: formData.notes || undefined,
    }

    // Add provider-specific config
    let requestData: CreateMailboxRequest | UpdateMailboxRequest

    if (formData.provider === 'AWS_SES') {
      requestData = {
        ...baseData,
        awsSesConfig: formData.awsSesConfig,
      }
    } else if (formData.provider === 'SMTP') {
      requestData = {
        ...baseData,
        smtpConfig: formData.smtpConfig,
      }
    } else {
      requestData = {
        ...baseData,
        sendgridConfig: formData.sendgridConfig,
      }
    }

    // Add status for updates
    if (isEditing) {
      ;(requestData as UpdateMailboxRequest).status = formData.status
    }

    try {
      if (isEditing) {
        await updateMutation.mutateAsync(requestData as UpdateMailboxRequest)
      } else {
        await createMutation.mutateAsync(requestData as CreateMailboxRequest)
      }
      onSuccess()
      onClose()
    } catch (error) {
      console.error('Failed to save mailbox:', error)
    }
  }

  const handleTestConnection = async () => {
    setTestStatus('testing')
    setTestMessage('')

    const testData = {
      provider: formData.provider,
      ...(formData.provider === 'AWS_SES' && { awsSesConfig: formData.awsSesConfig }),
      ...(formData.provider === 'SMTP' && { smtpConfig: formData.smtpConfig }),
      ...(formData.provider === 'SENDGRID' && { sendgridConfig: formData.sendgridConfig }),
    }

    try {
      const result = await testConnectionMutation.mutateAsync(testData)
      setTestStatus(result.success ? 'success' : 'error')
      setTestMessage(result.message)
    } catch (error: any) {
      setTestStatus('error')
      setTestMessage(error.response?.data?.message || 'Connection test failed')
    }
  }

  const updateField = <K extends keyof FormData>(field: K, value: FormData[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    setTestStatus('idle')
  }

  const updateAwsSesConfig = <K extends keyof AwsSesConfig>(field: K, value: AwsSesConfig[K]) => {
    setFormData((prev) => ({
      ...prev,
      awsSesConfig: { ...prev.awsSesConfig, [field]: value },
    }))
    setTestStatus('idle')
  }

  const updateSmtpConfig = <K extends keyof SmtpConfig>(field: K, value: SmtpConfig[K]) => {
    setFormData((prev) => ({
      ...prev,
      smtpConfig: { ...prev.smtpConfig, [field]: value },
    }))
    setTestStatus('idle')
  }

  const updateSmtpAuth = (field: 'user' | 'pass', value: string) => {
    setFormData((prev) => ({
      ...prev,
      smtpConfig: {
        ...prev.smtpConfig,
        auth: { ...prev.smtpConfig.auth, [field]: value },
      },
    }))
    setTestStatus('idle')
  }

  const updateImapConfig = <K extends keyof NonNullable<SmtpConfig['imap']>>(
    field: K,
    value: NonNullable<SmtpConfig['imap']>[K]
  ) => {
    setFormData((prev) => ({
      ...prev,
      smtpConfig: {
        ...prev.smtpConfig,
        imap: { ...prev.smtpConfig.imap!, [field]: value },
      },
    }))
    setTestStatus('idle')
  }

  const updateImapAuth = (field: 'user' | 'pass', value: string) => {
    setFormData((prev) => ({
      ...prev,
      smtpConfig: {
        ...prev.smtpConfig,
        imap: {
          ...prev.smtpConfig.imap!,
          auth: { ...prev.smtpConfig.imap!.auth, [field]: value },
        },
      },
    }))
    setTestStatus('idle')
  }

  const updateSendGridConfig = <K extends keyof SendGridConfig>(
    field: K,
    value: SendGridConfig[K]
  ) => {
    setFormData((prev) => ({
      ...prev,
      sendgridConfig: { ...prev.sendgridConfig, [field]: value },
    }))
    setTestStatus('idle')
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Mail className="text-primary" size={24} />
            <h2 className="text-xl font-bold text-gray-900">
              {isEditing ? 'Edit Mailbox' : 'Add New Mailbox'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Basic Information</h3>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => updateField('name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="e.g., Sales Team Mailbox"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => updateField('email', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="sales@example.com"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  From Name
                </label>
                <input
                  type="text"
                  value={formData.fromName}
                  onChange={(e) => updateField('fromName', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Sales Team"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reply-To Email
                </label>
                <input
                  type="email"
                  value={formData.replyToEmail}
                  onChange={(e) => updateField('replyToEmail', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="replies@example.com"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Daily Limit
                </label>
                <input
                  type="number"
                  min="1"
                  max="10000"
                  value={formData.dailyLimit}
                  onChange={(e) => updateField('dailyLimit', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Recommended: 30-40 emails/day for rotation
                </p>
              </div>

              {isEditing && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) =>
                      updateField('status', e.target.value as FormData['status'])
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="ACTIVE">Active</option>
                    <option value="INACTIVE">Inactive</option>
                    <option value="SUSPENDED">Suspended</option>
                    <option value="ERROR">Error</option>
                  </select>
                </div>
              )}
            </div>
          </div>

          {/* Provider Selection */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Email Provider</h3>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Provider Type <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-3 gap-3">
                <button
                  type="button"
                  onClick={() => updateField('provider', 'AWS_SES')}
                  className={`p-4 border-2 rounded-lg transition-all ${
                    formData.provider === 'AWS_SES'
                      ? 'border-primary bg-primary/10'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <Server size={24} className="mx-auto mb-2" />
                  <div className="font-medium">AWS SES</div>
                  <div className="text-xs text-gray-500">Amazon Simple Email Service</div>
                </button>

                <button
                  type="button"
                  onClick={() => updateField('provider', 'SMTP')}
                  className={`p-4 border-2 rounded-lg transition-all ${
                    formData.provider === 'SMTP'
                      ? 'border-primary bg-primary/10'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <Mail size={24} className="mx-auto mb-2" />
                  <div className="font-medium">SMTP</div>
                  <div className="text-xs text-gray-500">Generic SMTP Server</div>
                </button>

                <button
                  type="button"
                  onClick={() => updateField('provider', 'SENDGRID')}
                  className={`p-4 border-2 rounded-lg transition-all ${
                    formData.provider === 'SENDGRID'
                      ? 'border-primary bg-primary/10'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <Send size={24} className="mx-auto mb-2" />
                  <div className="font-medium">SendGrid</div>
                  <div className="text-xs text-gray-500">SendGrid API</div>
                </button>
              </div>
            </div>
          </div>

          {/* AWS SES Configuration */}
          {formData.provider === 'AWS_SES' && (
            <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
              <h4 className="font-medium text-gray-900">AWS SES Configuration</h4>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    AWS Region <span className="text-red-500">*</span>
                  </label>
                  <select
                    required
                    value={formData.awsSesConfig.region}
                    onChange={(e) => updateAwsSesConfig('region', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="us-east-1">US East (N. Virginia)</option>
                    <option value="us-west-2">US West (Oregon)</option>
                    <option value="eu-west-1">EU (Ireland)</option>
                    <option value="eu-central-1">EU (Frankfurt)</option>
                    <option value="ap-southeast-1">Asia Pacific (Singapore)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Configuration Set
                  </label>
                  <input
                    type="text"
                    value={formData.awsSesConfig.configurationSet}
                    onChange={(e) => updateAwsSesConfig('configurationSet', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Optional"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Access Key ID <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.awsSesConfig.accessKeyId}
                  onChange={(e) => updateAwsSesConfig('accessKeyId', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="AKIAIOSFODNN7EXAMPLE"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Secret Access Key <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  required
                  value={formData.awsSesConfig.secretAccessKey}
                  onChange={(e) => updateAwsSesConfig('secretAccessKey', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY"
                />
              </div>
            </div>
          )}

          {/* SMTP Configuration */}
          {formData.provider === 'SMTP' && (
            <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
              <h4 className="font-medium text-gray-900">SMTP Configuration</h4>

              <div className="grid grid-cols-3 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    SMTP Host <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.smtpConfig.host}
                    onChange={(e) => updateSmtpConfig('host', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="smtp.gmail.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Port <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    required
                    min="1"
                    max="65535"
                    value={formData.smtpConfig.port}
                    onChange={(e) => updateSmtpConfig('port', parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="587"
                  />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="smtp-secure"
                  checked={formData.smtpConfig.secure}
                  onChange={(e) => updateSmtpConfig('secure', e.target.checked)}
                  className="rounded border-gray-300 text-primary focus:ring-primary"
                />
                <label htmlFor="smtp-secure" className="text-sm font-medium text-gray-700">
                  Use TLS/SSL (Port 465 typically requires this)
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Username <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.smtpConfig.auth.user}
                  onChange={(e) => updateSmtpAuth('user', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="your-email@gmail.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  required
                  value={formData.smtpConfig.auth.pass}
                  onChange={(e) => updateSmtpAuth('pass', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="App password or account password"
                />
              </div>

              {/* IMAP Configuration */}
              <div className="pt-4 border-t border-gray-200">
                <h5 className="font-medium text-gray-900 mb-3">IMAP Configuration (Optional)</h5>
                <p className="text-sm text-gray-600 mb-4">
                  Configure IMAP to monitor replies and responses. Leave blank if only sending emails.
                </p>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      IMAP Host
                    </label>
                    <input
                      type="text"
                      value={formData.smtpConfig.imap?.host || ''}
                      onChange={(e) => updateImapConfig('host', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="imap.ionos.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      IMAP Port
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="65535"
                      value={formData.smtpConfig.imap?.port || 993}
                      onChange={(e) => updateImapConfig('port', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="993"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-2 mt-3">
                  <input
                    type="checkbox"
                    id="imap-secure"
                    checked={formData.smtpConfig.imap?.secure || true}
                    onChange={(e) => updateImapConfig('secure', e.target.checked)}
                    className="rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <label htmlFor="imap-secure" className="text-sm font-medium text-gray-700">
                    Use TLS/SSL (Port 993 typically requires this)
                  </label>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    IMAP Username
                  </label>
                  <input
                    type="text"
                    value={formData.smtpConfig.imap?.auth.user || ''}
                    onChange={(e) => updateImapAuth('user', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Usually same as SMTP username"
                  />
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    IMAP Password
                  </label>
                  <input
                    type="password"
                    value={formData.smtpConfig.imap?.auth.pass || ''}
                    onChange={(e) => updateImapAuth('pass', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Usually same as SMTP password"
                  />
                </div>
              </div>
            </div>
          )}

          {/* SendGrid Configuration */}
          {formData.provider === 'SENDGRID' && (
            <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
              <h4 className="font-medium text-gray-900">SendGrid Configuration</h4>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  API Key <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  required
                  value={formData.sendgridConfig.apiKey}
                  onChange={(e) => updateSendGridConfig('apiKey', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="SG.xxxxxxxxxxxxxxxxxxxxxxx"
                />
                <p className="text-xs text-gray-500 mt-1">
                  SendGrid API keys start with "SG."
                </p>
              </div>
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => updateField('notes', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Optional notes about this mailbox..."
            />
          </div>

          {/* Test Connection */}
          <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
            <button
              type="button"
              onClick={handleTestConnection}
              disabled={testStatus === 'testing'}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              {testStatus === 'testing' ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Testing...
                </>
              ) : (
                <>
                  <Send size={16} />
                  Test Connection
                </>
              )}
            </button>

            {testStatus === 'success' && (
              <div className="flex items-center gap-2 text-green-600">
                <CheckCircle2 size={20} />
                <span className="text-sm font-medium">{testMessage}</span>
              </div>
            )}

            {testStatus === 'error' && (
              <div className="flex items-center gap-2 text-red-600">
                <AlertCircle size={20} />
                <span className="text-sm font-medium">{testMessage}</span>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              {isLoading ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Saving...
                </>
              ) : (
                <>{isEditing ? 'Update Mailbox' : 'Create Mailbox'}</>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
